import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { saveSegmentThunk } from 'slices/aiConversations/thunks'
import type {
  ActivationChoice,
  ActivationFlowState,
  AISegmentBuilderState,
  MessageSegmentData,
} from 'slices/aiSegmentBuilder/types'

const initialActivationFlow: ActivationFlowState = {
  phase: 'idle',
  segmentName: '',
  pendingAutoApprove: null,
  flowOrigin: 'initial-save',
  rememberChoice: false,
  isSaving: false,
  error: null,
  instanceId: null,
}

const initialState: AISegmentBuilderState = {
  messageSegmentData: {},
  isCampaignLoading: false,
  isSegmentSaving: false,
  savedSegmentIds: [],
  campaignModal: {
    isOpen: false,
    segmentId: null,
  },
  segmentDetailsModal: {
    isOpen: false,
    segmentId: null,
    title: null,
  },
  conversationState: 'empty',
  initialPrompt: '',
  activationFlow: initialActivationFlow,
}

const aiSegmentBuilderSlice = createSlice({
  name: 'aiSegmentBuilder',
  initialState,
  reducers: {
    setMessageSegmentData: (
      state,
      action: PayloadAction<{
        messageId: string
        data: Partial<MessageSegmentData>
      }>
    ) => {
      const { messageId, data } = action.payload
      state.messageSegmentData[messageId] = {
        ...state.messageSegmentData[messageId],
        messageId,
        ...data,
      }
    },
    clearMessageSegmentData: (state, action: PayloadAction<string>) => {
      delete state.messageSegmentData[action.payload]
    },
    setCampaignLoading: (state, action: PayloadAction<boolean>) => {
      state.isCampaignLoading = action.payload
    },
    setSegmentSaving: (state, action: PayloadAction<boolean>) => {
      state.isSegmentSaving = action.payload
    },
    addSavedSegmentId: (state, action: PayloadAction<number>) => {
      if (!state.savedSegmentIds.includes(action.payload)) {
        state.savedSegmentIds.push(action.payload)
      }
    },
    openCampaignModal: (state, action: PayloadAction<number>) => {
      state.campaignModal.isOpen = true
      state.campaignModal.segmentId = action.payload
    },
    closeCampaignModal: state => {
      state.campaignModal.isOpen = false
      state.campaignModal.segmentId = null
    },
    openSegmentDetailsModal: (
      state,
      action: PayloadAction<{ segmentId: number; title?: string }>
    ) => {
      state.segmentDetailsModal.isOpen = true
      state.segmentDetailsModal.segmentId = action.payload.segmentId
      state.segmentDetailsModal.title = action.payload.title ?? null
    },
    closeSegmentDetailsModal: state => {
      state.segmentDetailsModal.isOpen = false
      state.segmentDetailsModal.segmentId = null
      state.segmentDetailsModal.title = null
    },
    closeDetailsAndOpenCampaignModal: (
      state,
      action: PayloadAction<number>
    ) => {
      state.segmentDetailsModal.isOpen = false
      state.segmentDetailsModal.segmentId = null
      state.segmentDetailsModal.title = null
      state.campaignModal.isOpen = true
      state.campaignModal.segmentId = action.payload
    },
    setConversationState: (
      state,
      action: PayloadAction<'empty' | 'chatting'>
    ) => {
      state.conversationState = action.payload
    },
    setInitialPrompt: (state, action: PayloadAction<string>) => {
      state.initialPrompt = action.payload
    },
    createAnotherSegment: state => {
      state.conversationState = 'empty'
      state.campaignModal.isOpen = false
      state.campaignModal.segmentId = null
      state.segmentDetailsModal.isOpen = false
      state.segmentDetailsModal.segmentId = null
      state.segmentDetailsModal.title = null
      state.initialPrompt = ''
      state.messageSegmentData = {}
      state.savedSegmentIds = []
      state.isSegmentSaving = false
      state.isCampaignLoading = false
      // activationFlow is intentionally preserved: resetting it would wipe the
      // session-sticky pendingAutoApprove that flowStarted/flowCompleted keep.
    },
    clearAllData: state => {
      state.messageSegmentData = {}
      state.savedSegmentIds = []
      state.isCampaignLoading = false
      state.isSegmentSaving = false
      state.campaignModal = {
        isOpen: false,
        segmentId: null,
      }
      state.segmentDetailsModal = {
        isOpen: false,
        segmentId: null,
        title: null,
      }
      state.conversationState = 'empty'
      state.initialPrompt = ''
      state.activationFlow = initialActivationFlow
    },
    flowStarted: (
      state,
      action: PayloadAction<{ initialName: string; instanceId: string }>
    ) => {
      state.activationFlow.phase = 'naming'
      state.activationFlow.segmentName = action.payload.initialName
      state.activationFlow.instanceId = action.payload.instanceId
      state.activationFlow.flowOrigin = 'initial-save'
      // pendingAutoApprove is intentionally NOT reset here — session stickiness:
      // a prior-session choice must survive flowStarted so the thunk can skip the
      // modal on repeat saves within the same session.
      state.activationFlow.rememberChoice = false
      state.activationFlow.isSaving = false
      state.activationFlow.error = null
    },
    nameEdited: (state, action: PayloadAction<string>) => {
      state.activationFlow.segmentName = action.payload
    },
    nameSubmitted: state => {
      state.activationFlow.phase = 'activating'
      state.activationFlow.flowOrigin = 'initial-save'
    },
    activationStarted: (
      state,
      action: PayloadAction<{
        origin: 'initial-save' | 'change-preference'
        seedAutoApprove?: 'activate' | 'save-only' | null
        seedRememberChoice?: boolean
      }>
    ) => {
      state.activationFlow.phase = 'activating'
      state.activationFlow.flowOrigin = action.payload.origin
      state.activationFlow.pendingAutoApprove =
        action.payload.seedAutoApprove ?? null
      state.activationFlow.rememberChoice =
        action.payload.seedRememberChoice ?? false
      state.activationFlow.isSaving = false
      state.activationFlow.error = null
    },
    activationOptionSelected: (
      state,
      action: PayloadAction<ActivationChoice>
    ) => {
      state.activationFlow.pendingAutoApprove = action.payload
    },
    rememberChoiceToggled: state => {
      state.activationFlow.rememberChoice = !state.activationFlow.rememberChoice
    },
    activationCancelled: state => {
      state.activationFlow.phase =
        state.activationFlow.flowOrigin === 'change-preference'
          ? 'naming'
          : 'idle'
      state.activationFlow.pendingAutoApprove = null
      state.activationFlow.rememberChoice = false
    },
    activationSaveStarted: state => {
      state.activationFlow.isSaving = true
      state.activationFlow.error = null
    },
    activationSaveFinished: state => {
      state.activationFlow.isSaving = false
      state.activationFlow.phase =
        state.activationFlow.flowOrigin === 'change-preference'
          ? 'naming'
          : 'done'
    },
    activationSaveFailed: (state, action: PayloadAction<string>) => {
      state.activationFlow.isSaving = false
      state.activationFlow.error = action.payload
    },
    flowReset: state => {
      state.activationFlow = initialActivationFlow
    },
    flowCompleted: state => {
      state.activationFlow.phase = 'done'
      state.activationFlow.isSaving = false
      // flowOrigin, pendingAutoApprove, rememberChoice intentionally preserved:
      // pendingAutoApprove for session stickiness; others reset on next flowStarted
    },
  },
  extraReducers: builder => {
    builder.addCase(saveSegmentThunk.rejected, state => {
      state.isSegmentSaving = false
    })
  },
})

export const {
  setMessageSegmentData,
  clearMessageSegmentData,
  setCampaignLoading,
  setSegmentSaving,
  addSavedSegmentId,
  openCampaignModal,
  closeCampaignModal,
  openSegmentDetailsModal,
  closeSegmentDetailsModal,
  closeDetailsAndOpenCampaignModal,
  setConversationState,
  setInitialPrompt,
  createAnotherSegment,
  clearAllData,
  flowStarted,
  nameEdited,
  nameSubmitted,
  activationStarted,
  activationOptionSelected,
  rememberChoiceToggled,
  activationCancelled,
  activationSaveStarted,
  activationSaveFinished,
  activationSaveFailed,
  flowReset,
  flowCompleted,
} = aiSegmentBuilderSlice.actions

export const reducer = aiSegmentBuilderSlice.reducer
export const selectAISegmentBuilder = (state: {
  aiSegmentBuilder: AISegmentBuilderState
}) => state.aiSegmentBuilder

export const selectIsSegmentSaving = (state: {
  aiSegmentBuilder: AISegmentBuilderState
}) => state.aiSegmentBuilder.isSegmentSaving

export const selectActivationFlow = (state: {
  aiSegmentBuilder?: AISegmentBuilderState
}) => state.aiSegmentBuilder?.activationFlow ?? initialActivationFlow
